import React from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import { AlertTriangle, CheckCircle, Clock, XCircle, RefreshCw } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

function StatusIcon({ status }) {
  if (status === "done") return <CheckCircle className="w-4 h-4 text-green-500" />;
  if (status === "error") return <XCircle className="w-4 h-4 text-red-500" />;
  if (status === "running") return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
  return <Clock className="w-4 h-4 text-gray-400" />;
}

function PillBadge({ status }) {
  const map = {
    done: "bg-green-100 text-green-800",
    error: "bg-red-100 text-red-800",
    running: "bg-blue-100 text-blue-800",
    queued: "bg-yellow-100 text-yellow-800",
    canceled: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

export default function BackupStatusPage() {
  const { data, error, mutate, isLoading } = useSWR(
    "/api/v1/resellers/me/backup-queue",
    fetcher,
    { refreshInterval: 10_000 },
  );

  if (isLoading) return <Spinner className="mt-12 mx-auto" />;
  if (error) return (
    <div className="p-6 text-red-600">Fehler: {formatApiError(error)}</div>
  );

  const runs = data?.runs || [];
  const failed = runs.filter((r) => r.status === "error");
  const running = runs.filter((r) => r.status === "running");

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Backup-Status</h1>
        <button onClick={() => mutate()} className="btn btn-sm btn-ghost">
          <RefreshCw className="w-4 h-4" /> Aktualisieren
        </button>
      </div>

      {failed.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          <span className="text-sm text-red-700">
            {failed.length} Backup-Lauf/Läufe mit Fehlern — bitte prüfen!
          </span>
        </div>
      )}

      <div className="mb-3 flex gap-4 text-sm text-gray-600">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-4 h-4 text-green-500" /> {runs.filter(r => r.status === "done").length} erfolgreich
        </span>
        {running.length > 0 && (
          <span className="flex items-center gap-1">
            <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" /> {running.length} läuft
          </span>
        )}
        {failed.length > 0 && (
          <span className="flex items-center gap-1">
            <XCircle className="w-4 h-4 text-red-500" /> {failed.length} fehlgeschlagen
          </span>
        )}
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Cloud</th>
              <th className="px-3 py-2">Phase</th>
              <th className="px-3 py-2">Fortschritt</th>
              <th className="px-3 py-2">Fehler</th>
              <th className="px-3 py-2">Zeit</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.run_id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1.5">
                    <StatusIcon status={r.status} />
                    <PillBadge status={r.status} />
                  </span>
                </td>
                <td className="px-3 py-2 font-medium">{r.domain}</td>
                <td className="px-3 py-2 text-gray-600">{r.phase || "—"}</td>
                <td className="px-3 py-2">
                  {r.status === "running" ? `${r.pct || 0}%` : "—"}
                </td>
                <td className="px-3 py-2 max-w-xs">
                  {r.error ? (
                    <span className="text-red-600 text-xs" title={r.error}>
                      {r.error.length > 80 ? r.error.slice(0, 80) + "…" : r.error}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-gray-400 text-xs">{fmtRel(r.created_at)}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                  Keine Backup-Läufe vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
